/**
 * The shapes a donut is drawn from, and the two rules that decide whether it may be drawn at all.
 *
 * ==========================================================================================
 * `dashboard.md` REFUSES A PIE, AND THIS IS NOT THE ONE IT REFUSES
 * ==========================================================================================
 *
 * That refusal is specific and it still stands. It is about the EXPENSE CATEGORIES:
 *
 *   "The parts are not exhaustive — the rows are the ten largest of up to seventeen with no
 *   remainder row — so no part-of-whole mark can be honest about them. A pie, a donut, a treemap and
 *   a 100% stacked bar are all refused for the one reason."
 *
 * And it states the rule that generalises: **a part-of-whole mark is honest only where the parts are
 * exhaustive.** The net worth composition satisfies it exactly — savings, investments, equity and
 * vehicles sum to net worth by construction, which is the same fact that lets the area chart draw a
 * total line over its own layers.
 *
 * The same document also anticipates this mark. Under the retired ring: "**If a future piece needs a
 * proportion-of-a-signed-whole and nothing more, this entry is the argument for rebuilding it** — it
 * is deliberately kept intact for that."
 *
 * ==========================================================================================
 * A NEGATIVE SLICE CANNOT BE DRAWN, AND THIS MODULE SAYS SO RATHER THAN CLAMPING
 * ==========================================================================================
 *
 * The ring's argument, one shape along: a pie facing a negative part "must either erase the negative
 * slice or draw one larger than its own circle". Both are lies, and the second is the worse one
 * because it looks deliberate.
 *
 * Net worth reaches that state honestly. Equity is property value minus the loan and goes negative
 * when a household is under water — the area chart CLAMPS its band at zero for exactly this and
 * names the clamped months, because a stacked area cannot draw a negative band either. A donut has
 * no equivalent of the clamp: a clamped slice would silently restate every OTHER slice's share.
 *
 * So `hasNegativeSlice` is a refusal rather than a repair, and the component that reads it renders a
 * sentence instead of a shape. `dashboard.md`'s standing requirement: "the most important sentence
 * about such a month should not require reading a shape."
 *
 * ==========================================================================================
 * NO REMAINDER IS SYNTHESISED, AND THAT IS THE OTHER HALF OF THE HONESTY
 * ==========================================================================================
 *
 * `funnel.ts` builds a closing band from `total − Σ shown` because its stages genuinely omit
 * categories. This module does NOT: the caller passes the parts and the whole is their sum, so there
 * is nothing to reconcile and nothing that can silently fail to add up. A caller with non-exhaustive
 * parts must not use this mark — that is what the refusal above is about.
 */

/** One part of the whole. Already-formatted figures stay with the caller. */
export interface DonutSlice {
  key: string;
  label: string;
  /** Signed. A negative one is what `hasNegativeSlice` refuses; see the header. */
  cents: bigint;
}

/**
 * Nothing to draw: no slices, or every slice is zero.
 *
 * Checked BEFORE the lazy boundary, so a household with nothing recorded never downloads Recharts —
 * the rule every chart in this package follows.
 */
export function isEmptyDonut(slices: readonly DonutSlice[]): boolean {
  return slices.length === 0 || slices.every((slice) => slice.cents === 0n);
}

/**
 * At least one part is negative, so no part-of-whole mark can state these honestly.
 *
 * SEPARATE FROM `isEmptyDonut` because the two mean different things to a reader and deserve
 * different sentences: "nothing recorded yet" invites an action, "your debts exceed one of these
 * pillars" is a fact about the household's position.
 */
export function hasNegativeSlice(slices: readonly DonutSlice[]): boolean {
  return slices.some((slice) => slice.cents < 0n);
}

/** The whole. The sum of the parts and never a figure passed in beside them — see the header. */
export function donutTotalCents(slices: readonly DonutSlice[]): bigint {
  return slices.reduce((total, slice) => total + slice.cents, 0n);
}

/**
 * Each slice's share, as a whole number of percent.
 *
 * **They may sum to 99 or 101 and are NOT adjusted**, which is `assetShares`' rule in
 * `@gladis/reporting` and is settled by FR-018a: forcing them to 100 makes at least one label
 * disagree with the euro figure beside it, and decimal places give precision the household cannot
 * use. Nothing on screen prints a total of them, so nothing asserts that they add up.
 *
 * Computed from CENTS, never from rounded euros — NFR-005.
 */
export function donutShares(slices: readonly DonutSlice[]): Map<string, number> {
  const total = donutTotalCents(slices);
  const shares = new Map<string, number>();
  if (total <= 0n) return shares;

  for (const slice of slices) {
    shares.set(slice.key, Number((slice.cents * 1000n) / total) / 10);
  }
  return shares;
}
